import Axios from "axios";
import { store } from "./redux/store";
import { EClientPlatform } from "./utils";
import { createAuthHeader, isValidTokenFormat } from "./utils/auth-headers";
import { getCorrelationId } from "./utils/correlation-id";
import { clearSessionAndRedirect, isInvalidSignatureResponse, isTokenExpiredResponse, refreshTokenOnce } from "./gateways/refreshTokenOnce";

const apiBaseUrl = (process.env.REACT_APP_API_URL ?? "http://localhost:3001").replace(/\/+$/, "");
const apiVersion = process.env.REACT_APP_API_VERSION ?? "v1";
const API_BASE_URL = `${apiBaseUrl}/${apiVersion}`;

export const refreshGateway = Axios.create({
  baseURL: API_BASE_URL,
  timeout: 90000,
});

export function ResponseError(code, status, message, data = undefined) {
  this.code = code;
  this.status = status;
  this.message = message;
  this.data = data;
}


const axiosInstance = Axios.create({
  baseURL: API_BASE_URL,
  timeout: 90000,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("token");
    const companyId =
      store.getState().userState.activeCompanyId ?? sessionStorage.getItem("activeCompanyId");

    config.headers = config.headers || {};

    if (companyId && !config.headers["x-company-id"]) {
      config.headers["x-company-id"] = String(companyId);
    }

    config.headers["x-correlation-id"] = getCorrelationId();
    config.headers["x-client-platform"] = EClientPlatform.WEB;

    if (token && isValidTokenFormat(token)) {
      Object.assign(config.headers, createAuthHeader(token));
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response) {
      throw new ResponseError(
        "ERR_CONNECTION_REFUSED",
        503,
        "Service is unavailable, try later",
      );
    }

    const originalRequest = error.config;

    originalRequest.headers = originalRequest.headers || {};

    const status = error.response.status;

    const hasStoredTokens =
      Boolean(sessionStorage.getItem("token")) &&
      Boolean(sessionStorage.getItem("refresh_token"));

    const isRefreshRequest =
      typeof originalRequest?.url === "string" &&
      originalRequest.url.includes("/tokens/refresh");

    const isValidateRequest =
      typeof originalRequest?.url === "string" &&
      originalRequest.url.includes("/tokens/validate");

    if (status === 401 && isInvalidSignatureResponse(error)) {
      console.warn("Token signature mismatch detected. Clearing local tokens.");
      clearSessionAndRedirect();
      return Promise.reject(error);
    }

    const shouldRefresh =
      status === 401 &&
      hasStoredTokens &&
      !originalRequest._retry &&
      !isRefreshRequest &&
      !isValidateRequest &&
      isTokenExpiredResponse(error);

    if (shouldRefresh) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshTokenOnce();

        Object.assign(originalRequest.headers, createAuthHeader(newToken));

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.warn("Token refresh failed. Clearing tokens.");
        clearSessionAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export const gatewayCharts = Axios.create({
  baseURL: `${process.env.REACT_APP_API_URL_CHARTS ?? "http://localhost:3005"}`,
  timeout: 90000,
});

export const gateway = axiosInstance;
